// vuex
import store from "@/store/index";
// libraries
import moment from "moment";
// internal
import { processAssignment, getCustomerPriceList, processAssignmentsList } from "@/utils/newDbUtils";
import { publishMessage } from "@/utils/slackUtils";
import { mapAttachedDocuments } from "@/utils/assignmentDocumentUtils";
import {
  getFCWorkType,
  getFCWordCount,
  getFCRequestType,
  isAdaptation,
} from "@/utils/spreadsheetUploadUtils";

export const BLANK_NEW_ASSIGNMENT_DATAS = [
  {
    project: null,
    projectManager: null,
    clientRequester: null,
    clientDeliveryDate: null,
    clientDeliveryDue: null,
    timeZone: null,
    primary: null,
    secondary: null,
    qc: null,
    department: null,
    assignmentDetails: {
      assignmentId: null,
      assignmentDetail: null,
      assignmentTitle: null,
      clientFeedback: null,
      characterCount: null,
      fileLocation: null,
      genre: null,
      geo: null,
      taskId: null,
      sourceGeo: null,
      mediaType: null,
      primaryAccepted: null,
      secondaryAccepted: null,
      assignmentStatus: "Assigned",
      wordCount: null,
      contentId: null,
      emailSubjectLine: null,
      keyword: null,
      billed: null,
      clientReviewed: null,
      note: null,
      requestType: null,
      urgent: null,
      link: null,
      slackTs: null
    },
    pricing: {
      purchaseOrder: null,
      workTypes: [{ customerPriceListId: null, quantity: null, orderPosition: 1 }],
    },
    task: {
      taskId: null,
      id: null,
    },
    document: null,
    schedule: {
      deliveryDate: null,
      deliveryTime: null,
      payPeriod: null,
      primaryDueDate: null,
      primaryDueTime: null,
      requestDate: moment(
        new Date().toLocaleString("en-US", {
          timeZone: "America/New_York",
        })
      ).format("YYYY-MM-DD"),
      secondaryDueDate: null,
      secondaryDueTime: null,
      qcDueDate: null,
      qcDueTime: null,
    },
  },
];

export const BLANK_DUB_EDITOR_DATAS = {
  id: null,
  assignmentId: null,
  primaryFragments: {
    fragments: [
      {
        id: null,
        fragmentId: null,
        text: ""
      }
    ],
    staffId: null,
    complete: false
  }
};

// canonical "empty" assignment form — used for the store's initial state and
// to reset the form once the assignment modal is closed
export const getBlankAssignmentForm = () => ({
  id: null,
  // `taskId` is the human-readable Cue Task ID; `taskDbId` is the record id the
  // /task endpoints expect. The hierarchy panel needs the latter to load the
  // parent task and its sibling assignments.
  taskId: null,
  taskDbId: null,
  // Server-assigned key for the assignment group under a task. Read-only here —
  // it is what promotes the parent task once the siblings all reach a status.
  cueTaskId: null,
  assignmentTitle: null,
  // The assignment description the dashboard table and the Slack notification
  // show. Distinct from `assignmentTitle`.
  assignmentDetail: null,
  // The client's own id for the assignment (assignmentDetails.assignmentId).
  externalAssignmentId: null,
  contentId: null,
  requestType: null,
  assignmentType: null,
  sourceGeo: null,
  geo: null,
  taskName: "",
  department: null,
  note: null,
  urgent: null,
  // Carried through so a full update never blanks values the form has no editor
  // for. Only the API and the staff-facing views write these.
  clientFeedback: null,
  clientReviewed: null,
  slackTs: null,
  primaryAccepted: null,
  secondaryAccepted: null,
  payPeriod: "",
  status: "Requested",
  isBilled: false,
  priority: null,
  fileLocation: null,
  emailSubjectLine: "",
  mediaType: null,
  keyword: null,
  genre: "",
  wordCount: "",
  characterCount: "",
  relatedProject: {
    name: "Empty",
  },
  clientRequester: {
    user: {
      firstName: "Empty",
      lastName: "",
    },
  },
  purchaseOrder: null,
  projectManager: null,
  overrideStaffAvailability: false,
  overrideDueDates: false,
  deliveryDate: null,
  deliveryTime: null,
  requestDate: null,
  requestTime: null,
  primary: null,
  secondary: null,
  qc: null,
  schedule: {
    primaryDueDate: null,
    primaryDueTime: null,
    secondaryDueDate: null,
    secondaryDueTime: null,
    qcDueDate: null,
    qcDueTime: null,
  },
  pricingModel: null,
  pricing: {
    purchaseOrder: null,
    workTypes: [],
  },
  // Notes & Files tab. `documents` holds both the files already attached to the
  // assignment (uploaded: true) and the ones the user just picked, which are
  // kept here until the assignment is saved. `removedDocuments` holds the stored
  // files the user dropped, which are detached on the next save — until then the
  // removal is only local, so closing the form undoes it. `links` holds plain
  // URL strings.
  documents: [],
  removedDocuments: [],
  links: [],
});

export const amountOfEngilshAdaptations = (geos) => {
  const enAdaptations = ["en-GB", "en-CA", "en-AU"];
  return geos.every((geo) => enAdaptations.includes(geo));
};

//////////////////////////////
// assignment CRUD
//////////////////////////////
export const formatBackendData = (data) => {
  const retVal = {
    ...data,
    clientId: data.project?.client?.id,
    clientRequesterId: data.clientRequester?.user?.id,
    pricing: {
      purchaseOrderId: data.pricing?.purchaseOrder === -1 ?
        -1 :
        data.pricing?.purchaseOrder?.id ?? null,
      workTypes: data.pricing?.workTypes?.map((el) => ({
        customerPriceListId: el?.customerPriceList ? el?.customerPriceList?.id : null,
        quantity: el?.quantity || el?.quantity === 0 ? +el?.quantity : null,
        orderPosition: el?.orderPosition
      })),
    },
    primaryId: data.primary?.user?.id,
    secondaryId: data.secondary?.user?.id ?? null,
    qcId: data.qc?.user?.id ?? null,
    projectId: data.project?.id,
    projectManagerId: store.getters["assignment/bulkEditing"]
      ? data.projectManager?.user?.id
      : data.project?.projectManager?.id ?? null,
    writerSlackID:
      data.primary?.adminDetails?.[
        `slackId${data.project?.name === "iTunes FC" ? "Fc" : "Staff"}`
      ],
    editorSlackID:
      data.secondary?.adminDetails?.[
        `slackId${data.project?.name === "iTunes FC" ? "Fc" : "Staff"}`
      ],
    reviewerSlackID:
      data.qc?.adminDetails?.[
        `slackId${data.project?.name === "iTunes FC" ? "Fc" : "Staff"}`
      ],
  };
  if (retVal && retVal.schedule && !retVal.schedule?.requestTime) {
    retVal.schedule.requestTime = "10:00 AM";
  }
  // make clean PATCH object for bulk edit
  const cleanObject = (object) => {
    Object.entries(object).forEach(([k, v]) => {
      if (v && typeof v === "object") cleanObject(v);
      if (
        (v && typeof v === "object" && !Object.keys(v).length) ||
        v === null ||
        v === undefined ||
        (Array.isArray(v) && v.length === 0)
      ) {
        if (Array.isArray(object)) object.splice(k, 1);
        else if (!(v instanceof Date)) delete object[k];
      }
    });
    return object;
  };
  if (store.getters["assignment/bulkEditing"]) cleanObject(retVal);
  return retVal;
};

export const processAssignmentsWithDatas = async (datas) => {
  const backendUploadDatas = datas.map((data) => {
    let assignment = { ...formatBackendData(data) };
    return assignment;
  });
  backendUploadDatas.forEach( object => {
    if (object.pricing.workTypes.length === 1 && object.pricing.workTypes[0].quantity === 0 && !object.pricing.workTypes[0].hasOwnProperty('customerPriceListId')) {
      object.pricing.workTypes = null;
    }
    if (object.assignmentDetails && object.assignmentDetails.assignmentStatus === "Pending Hours") {
      if (object.pricing.workTypes[0].quantity === null) {
        object.pricing.workTypes[0].quantity = 0;
      }
    }
  })
  try {
    const serverResponse = await addAssignmentsFromForm(backendUploadDatas);
    // process the array of API respones for flash message
    const rejected = serverResponse.filter((el) => el.status >= 400);
    let rejectedResponse = null;
    if (rejected.length) {
      const errMsg = rejected.map((el) => el.data.message).toString();
      rejectedResponse = { data: { statusCode: 500, message: errMsg } };
    }
    return rejectedResponse ?? { data: { statusCode: 200 } };
  } catch (err) {
    console.error(err);
    return err;
  }
};

const addAssignmentsFromForm = async (datas) => {
  const projectsWithoutEditModeMessage = ["Harman Product Marketing", "iTunes Film/TV Writeups", "iTunes - Apple Books"];
  const requestTypeWithoutMessage = ["Project Management", "Development", "Administrative"];
  let retVal = [];
  for (let i = 0; i < datas.length; i++) {
    try {
      const resp = await processAssignment({
        data: datas[i],
        id: datas[i]?.id,
        batchEdit: store.getters["assignment/bulkEditing"],
      });
      retVal.push(resp);
      if (
        (resp.status === 200 || resp.status === 201) &&
        !store.getters["assignment/bulkEditing"]
      ) {
        // process the GEO-related data for Slack notification
        const [writer, editor, reviewer] = [
          datas[i].writerSlackID
            ? `<@${datas[i].writerSlackID}>`
            : `${datas[i].primary.user.firstName} ${datas[i].primary.user.lastName}`,
          datas[i].editorSlackID
            ? `<@${datas[i].editorSlackID}>`
            : datas[i].secondary
            ? `${datas[i].secondary.user.firstName} ${datas[i].secondary.user.lastName}`
            : null,
          datas[i].reviewerSlackID
            ? `<@${datas[i].reviewerSlackID}>`
            : datas[i].qc
            ? `${datas[i].qc.user.firstName} ${datas[i].qc.user.lastName}`
            : null,
        ];
        let geoObj = {
          [datas[i].assignmentDetails.geo]: {
            assignments: [datas[i].assignmentDetails.assignmentDetail],
          },
        };
        if (writer)
          Object.assign(geoObj[datas[i].assignmentDetails.geo], {
            writers: [writer],
          });
        if (editor)
          Object.assign(geoObj[datas[i].assignmentDetails.geo], {
            editors: [editor],
          });
        if (reviewer)
          Object.assign(geoObj[datas[i].assignmentDetails.geo], {
            reviewers: [reviewer],
          });
        if (!datas[i]?.id && !datas[i].project?.dubEditorProject) {
          const geoKeys = [datas[i].assignmentDetails?.geo];
          if (geoKeys 
            && geoKeys.length 
            && geoKeys.includes("de-CH")
            && datas[i].assignmentDetails.requestType === "Adaptation") {
            if ((!projectsWithoutEditModeMessage.includes(datas[0].project.name) 
              ? true
              : store.getters["assignment/isEditMode"]
                ? false
                : true) && !requestTypeWithoutMessage.includes(datas[0].assignmentDetails.requestType)) await publishMessage({
              mode:
                datas[i].project?.name === "iTunes FC"
                  ? "fcNotification"
                  : "nonFcNotification",
              item: {
                ...datas[i],
                assignment: datas[i].assignmentDetails.assignmentDetail,
                geos: [geoObj],
                id: resp.data.id,
              },
              modifier: "de-CH-adaptation",
            });
          } else {
            if ((!projectsWithoutEditModeMessage.includes(datas[0].project.name) 
              ? true
              : store.getters["assignment/isEditMode"]
                ? false
                : true) && !requestTypeWithoutMessage.includes(datas[0].assignmentDetails.requestType)) await publishMessage({
              mode:
                datas[i].project?.name === "iTunes FC"
                  ? "fcNotification"
                  : "nonFcNotification",
              item: {
                ...datas[i],
                assignment: datas[i].assignmentDetails.assignmentDetail,
                geos: [geoObj],
                id: resp.data.id,
              },
            });
          }
        } else if (datas[i]?.id 
          && datas[i].project?.name !== "iTunes FC"
          && !datas[i].project?.dubEditorProject) {
            if ((!projectsWithoutEditModeMessage.includes(datas[0].project.name) 
              ? true
              : store.getters["assignment/isEditMode"]
                ? false
                : true) && !requestTypeWithoutMessage.includes(datas[0].assignmentDetails.requestType)) await publishMessage({
            mode: "nonFcNotification",
            item: {
              ...datas[i],
              assignment: datas[i].assignmentDetails.assignmentDetail,
              geos: [geoObj],
              id: resp.data.id,
            },
          });
        }
      }
    } catch (err) {
      console.error(err);
      retVal.push(err);
    }
  }

  if (datas.length 
      && datas[0]?.project?.dubEditorProject
      && (!projectsWithoutEditModeMessage.includes(datas[0].project.name) 
        ? true
        : store.getters["assignment/isEditMode"]
          ? false
          : true) && !requestTypeWithoutMessage.includes(datas[0].assignmentDetails.requestType)) {
    const dubEditorItem = {
      titlesList: [...new Set(datas.map((item) => item.assignmentDetails.assignmentTitle))],
      primaryDue: `${datas[0].schedule.primaryDueDate.split('-').reverse().slice(0, 2).reverse().join('/')}/${datas[0].schedule.primaryDueDate.split('-')[0]} ${datas[0].schedule.primaryDueTime}`,
      secondaryDue: datas[0].schedule.secondaryDueDate ? `${datas[0].schedule.secondaryDueDate.split('-').reverse().slice(0, 2).reverse().join('/')}/${datas[0].schedule.secondaryDueDate.split('-')[0]} ${datas[0].schedule.secondaryDueTime}` : null,
      qcDue: datas[0].schedule.qcDueDate ? `${datas[0].schedule.qcDueDate.split('-').reverse().slice(0, 2).reverse().join('/')}/${datas[0].schedule.qcDueDate.split('-')[0]} ${datas[0].schedule.qcDueTime}` : null,
    };
    const updatedDatas = datas.map((item) => {
      if (item.primary) {
        item.primary.cueSlackID = item.primary.adminDetails.slackIdStaff;
        item.primary.firstName = item.primary.user.firstName;
        item.primary.lastName = item.primary.user.lastName;
      }
      if (item.secondary) {
        item.secondary.cueSlackID = item.secondary.adminDetails.slackIdStaff;
        item.secondary.firstName = item.secondary.user.firstName;
        item.secondary.lastName = item.secondary.user.lastName;
      }
      if (item.qc) {
        item.qc.cueSlackID = item.qc.adminDetails.slackIdStaff;
        item.qc.firstName = item.qc.user.firstName;
        item.qc.lastName = item.qc.user.lastName;
      }
      return {...item};
    })
    publishMessage({
      mode: "dubEditorMessages",
      item: dubEditorItem,
      data: updatedDatas
    })
  }
  return retVal;
};

export const addAssignmentRowsToDB = (
  formValues,
  tableRows,
  staffAssignments,
  staffDatas
) => {
  return new Promise(async (resolve, reject) => {
    const priceList = await getCustomerPriceList();

    const shakespeare = store.getters["spreadsheetUpload/shakespeare"],
      shakespeareLoc = store.getters["spreadsheetUpload/shakespeareLoc"],
      isDubEditor = store.getters["spreadsheetUpload/dubEditor"],
      backstageCopywriting =
        store.getters["spreadsheetUpload/backstageCopywriting"],
      cueLoc = store.getters["spreadsheetUpload/cueLoc"],
      baseLocalization = store.getters["spreadsheetUpload/baseLocalization"],
      applyLocMinRate = store.getters["spreadsheetUpload/applyLocMinRate"],
      cueCopywriting = store.getters["spreadsheetUpload/cueCopywriting"],
      geosWithIndividualDeadlines = formValues.individualGeosDeadlines.map(
        (geo) => Object.keys(geo)[0]
      );

    let promiseList = [];

    // form the Slack notification data
    let notification = {
      project: formValues.relatedProject,
      assignment: tableRows[0].task_name,
      assignmentDetails: {
        keyword: tableRows[0].keyword,
        mediaType: shakespeare ? formValues.mediaType : tableRows[0].media_type,
        requestType: tableRows[0].request_type,
        fileLocation: formValues.fileLocation,
        assignmentTitle: formValues.assignmentTitle,
        wordCount: tableRows[0].word_count,
      },
      schedule: {
        payPeriod: formValues.payPeriod,
        primaryDueDate: formValues.writingDueDate,
        primaryDueTime: formValues.writingDue,
        secondaryDueDate: formValues.editingDueDate,
        secondaryDueTime: formValues.editingDue,
        qcDueDate: formValues.reviewingDueDate,
        qcDueTime: formValues.reviewingDue,
        deliveryDate: formValues.deliveryDate,
      },
      geos: [],
    };
    // init the Non-FC notifications
    let nonFcNotifications = [];

    const getGeoIndex = (geo, array) =>
        array.findIndex((el) => el.hasOwnProperty(geo)),
      applyIndividualGeoDeadlines = () => {
        const geosDeadlines = [...formValues.individualGeosDeadlines];
        geosWithIndividualDeadlines.forEach((geo) => {
          Object.assign(
            notification.geos[getGeoIndex(geo, notification.geos)][geo],
            {
              writingDueDate:
                geosDeadlines[getGeoIndex(geo, geosDeadlines)][geo]
                  .writingDueDate,
              writingDue:
                geosDeadlines[getGeoIndex(geo, geosDeadlines)][geo].writingDue,
              editingDueDate:
                geosDeadlines[getGeoIndex(geo, geosDeadlines)][geo]
                  .editingDueDate,
              editingDue:
                geosDeadlines[getGeoIndex(geo, geosDeadlines)][geo].editingDue,
              reviewingDueDate:
                geosDeadlines[getGeoIndex(geo, geosDeadlines)][geo]
                  .reviewingDueDate,
              reviewingDue:
                geosDeadlines[getGeoIndex(geo, geosDeadlines)][geo]
                  .reviewingDue,
            }
          );
        });
      };

    if (isDubEditor) {
      const createDubEditorSection = (rowData) => {
        let dubEditorAssignment = {};
        ["primaryFragments", "secondaryFragments", "qcFragments"].forEach((item) => {
          if (item === "primaryFragments" && rowData.primary) {
            dubEditorAssignment[item] = {
              staffId: staffDatas.find((primary) => primary.name === rowData.primary).user.id,
              fragments: formValues.relatedProject.dubEditor.fragments.map((fragment) => {
                return {
                  ...fragment,
                  text: "",
                  fragmentId: fragment.id
                };
              }),
              complete: false,
            }
          } else if (item === "secondaryFragments" && rowData.secondary) {
            dubEditorAssignment[item] = {
              staffId: staffDatas.find((secondary) => secondary.name === rowData.secondary).user.id,
              fragments: formValues.relatedProject.dubEditor.fragments.map((fragment) => {
                return {
                  ...fragment,
                  text: "",
                  fragmentId: fragment.id
                };
              }),
              complete: false,
            }
          } else if (item === "qcFragments" && rowData.qc) {
            dubEditorAssignment[item] = {
              staffId: staffDatas.find((qc) => qc.name === rowData.qc).user.id,
              fragments: formValues.relatedProject.dubEditor.fragments.map((fragment) => {
                return {
                  ...fragment,
                  text: "",
                  fragmentId: fragment.id
                };
              }),
              complete: false,
            }
          }
        })
        return dubEditorAssignment;
      };
      tableRows.forEach((tableRow) => {
        promiseList.push({
          projectId: formValues.relatedProject.id,
          department: tableRow.department ? 
            store.getters["assignment/departmentOptions"].find((el) => el.value === tableRow.department)?.id : 
            null,
          schedule: {
            deliveryDate: formValues.deliveryDate,
            deliveryTime: formValues.deliveryDue,
            payPeriod: formValues.payPeriod,
            primaryDueDate: formValues.writingDueDate,
            primaryDueTime:formValues.writingDue,
            secondaryDueDate: formValues.editingDueDate,
            secondaryDueTime: formValues.editingDue,
            qcDueDate: formValues.reviewingDueDate,
            qcDueTime: formValues.reviewingDue,
            requestDate: formValues.requestDate,
            requestTime: formValues.requestTime,
          },
          task: {
            taskId: tableRow.task_id,
          },
          dubEditorAssignment: createDubEditorSection(tableRow),
          assignmentDetails: {
            assignmentDetail: tableRow.assignment_detail,
            assignmentId: null,
            assignmentStatus: tableRow.status,
            assignmentTitle: tableRow.assignment_title,
            contentId: null,
            emailSubjectLine: null,
            fileLocation: tableRow.file_location,
            genre: null,
            geo: tableRow.geo,
            keyword: null,
            link: tableRow.link,
            mediaType: tableRow.mediaType,
            note: formValues.note,
            requestType: tableRow.requestType,
            sourceGeo: tableRow.source_geo,
            taskId: tableRow.task_id,
            urgent: formValues.urgent,
            characterCount: null,
            wordCount: null,
          },
          clientId: formValues.relatedProject.client?.id ?? null,
          clientRequesterId:
            formValues.relatedProject.clientRequesters?.find((req) =>
              tableRow.clientRequester.includes(req.user.firstName) &&
                  tableRow.clientRequester.includes(req.user.lastName)
            )?.user?.id ?? null,
          projectManagerId:
            formValues.relatedProject.projectManager?.id ?? null,
          primaryId: staffDatas.find((primary) => primary.name === tableRow.primary).user.id,
          secondaryId: tableRow.secondary
            ? staffDatas.find((secondary) => secondary.name === tableRow.secondary).user.id
            : null,
          qcId: tableRow.qc
            ? staffDatas.find((qc) => qc.name === tableRow.qc).user.id
            : null,
          pricing: {
            purchaseOrderId: formValues.po?.id ?? null,
            workTypes: [{
              customerPriceListId: store.getters["projects/gGetCustomerPriceList"].find(el => el.workTypeName === tableRow.work_type).id,
              quantity: +tableRow.quantity,
              orderPosition: 1
            }],
          },
        },
      );
      })
    } else 
    tableRows.forEach((tableRow) => {
      (shakespeare || cueCopywriting ? [tableRow.geo] : tableRow.geos).forEach(
        (geoVal) => {
          // set processed geo and individual deadlines
          const geo = shakespeareLoc || cueLoc ? geoVal.geo : geoVal,
            geoIndividualData =
              formValues.individualGeosDeadlines[
                getGeoIndex(geo, formValues.individualGeosDeadlines)
              ]?.[geo];
          // process work types
          const workTypes = [1, 2, 3, 4, 5, 6].map((wtype) => ({
            customerPriceListId:
              priceList.find(
                (price) =>
                  price.workTypeName ===
                  ((baseLocalization && applyLocMinRate !== 'Default' && !isAdaptation({
                    requestType: tableRow.request_type,
                    locale: geo,
                    allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                    sourceGeo: tableRow.source_geo
                  }))
                    ? staffAssignments[tableRow.task_name][geo][
                        `workType${wtype}Reference`
                      ]
                    : (shakespeare && !isAdaptation({
                    locale: geo,
                    allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                    sourceGeo: tableRow.source_geo,
                    requestType: tableRow.request_type,
                    tableRows: tableRows,
                    shakespeare: shakespeare,
                  })) || cueCopywriting
                    ? staffAssignments[tableRow.task_id][geo][
                        `workType${wtype}Reference`
                      ]
                      ? staffAssignments[tableRow.task_id][geo][
                          `workType${wtype}Reference`
                        ]
                      : null
                    : backstageCopywriting ||
                      (tableRow.request_type === "Adaptation" && !cueLoc) || 
                      (tableRow.request_type === "Localization" && isAdaptation({locale: geo, allLocales: tableRow.geos, requestType: tableRow.request_type, sourceGeo: tableRow.source_geo}))
                    ? getFCWorkType({
                        workType:
                          staffAssignments[tableRow.task_name][geo][
                            `workType${wtype}Reference`
                          ],
                        workType1: wtype === 1,
                        locale: geo,
                        allLocales: tableRow.geos,
                        sourceGeo: tableRow.source_geo,
                        adaptationRequest:
                          tableRow.request_type === "Adaptation",
                        requestType: tableRow.request_type,
                        tableRows: tableRows,
                        shakespeare: shakespeare,
                        backstageCopywriting: backstageCopywriting
                      })
                    : wtype === 1
                    ? cueLoc
                      ? formValues.workType
                      : getFCWorkType({
                          locale: geo,
                          allLocales: shakespeare ? tableRows.map((el) => el.geo) : null,
                          sourceLocale: tableRow.source_geo,
                          localizationMode: true,
                          sourceGeo: tableRow.source_geo,
                          shakespeare: shakespeare,
                          requestType: tableRow.request_type,
                          tableRows: tableRows,
                        })
                    : shakespeare && wtype === 1
                    ? getFCWorkType({
                        workType: null,
                        workType1: wtype === 1,
                        locale: geo,
                        allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                        sourceGeo: tableRow.source_geo,
                        shakespeare: shakespeare,
                        requestType: tableRow.request_type,
                        tableRows: tableRows,
                        adaptationRequest:
                        tableRow.request_type === "Adaptation",
                      }) : null)
              )?.id ?? null,
            quantity:
              (baseLocalization && applyLocMinRate !== 'Default' && !isAdaptation({
                requestType: tableRow.request_type,
                locale: geo,
                allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                sourceGeo: tableRow.source_geo
              }))
                ? staffAssignments[tableRow.task_name][geo][
                  `workType${wtype}Quantity`
                ]
              : (shakespeare && !isAdaptation({
                locale: geo,
                allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                sourceGeo: tableRow.source_geo,
                requestType: tableRow.request_type,
                tableRows: tableRows,
                shakespeare: shakespeare,
              })) || cueCopywriting
                ? staffAssignments[tableRow.task_id][geo][
                    `workType${wtype}Quantity`
                  ]
                  ? staffAssignments[tableRow.task_id][geo][
                      `workType${wtype}Quantity`
                    ]
                  : null
                : backstageCopywriting
                ? getFCWordCount({
                    wordCount:
                      staffAssignments[tableRow.task_name][geo][
                        `workType${wtype}Quantity`
                      ],
                    locale: geo,
                    allLocales: tableRow.geos,
                  })
                : wtype === 1
                ? tableRow.request_type === "Adaptation" && !cueLoc && !amountOfEngilshAdaptations(tableRow.geos)
                  ? null
                  : tableRow.word_count
                : null,
            orderPosition: wtype
          }));
          // form assignments list for POST
          promiseList.push({
              projectId: formValues.relatedProject.id,
              department: tableRow.department ? 
                store.getters["assignment/departmentOptions"].find((el) => el.value === tableRow.department)?.id : 
                shakespeare || shakespeareLoc ?
                  store.getters["assignment/departmentOptions"].find((el) => el.value === "FC")?.id :
                  null,
              schedule: {
                deliveryDate: formValues.deliveryDate,
                deliveryTime: formValues.deliveryDue,
                payPeriod: formValues.payPeriod,
                primaryDueDate:
                  geoIndividualData?.writingDueDate ??
                  formValues.writingDueDate,
                primaryDueTime:
                  geoIndividualData?.writingDue ?? formValues.writingDue,
                secondaryDueDate: staffAssignments[
                  tableRow[
                    `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                  ]
                ][geo].editor
                  ? geoIndividualData?.editingDueDate ??
                    formValues.editingDueDate ??
                    null
                  : null,
                secondaryDueTime: staffAssignments[
                  tableRow[
                    `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                  ]
                ][geo].editor
                  ? geoIndividualData?.editingDue ?? formValues.editingDue
                  : null,
                qcDueDate: staffAssignments[
                  tableRow[
                    `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                  ]
                ][geo].reviewer
                  ? geoIndividualData?.reviewingDueDate ??
                    formValues.reviewingDueDate ??
                    null
                  : null,
                qcDueTime: staffAssignments[
                  tableRow[
                    `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                  ]
                ][geo].reviewer
                  ? geoIndividualData?.reviewingDue ?? formValues.reviewingDue
                  : null,
                requestDate: moment(tableRow.request_date).format(
                  "YYYY-MM-DD"
                ),
                requestTime:
                  shakespeare || cueLoc || cueCopywriting
                    ? formValues.requestTime
                    : formValues.requestTime
                      ? formValues.requestTime
                      : "10:00 AM",
              },
              task: {
                taskId: shakespeare
                ? tableRow.content_id
                  ? tableRow.content_id
                  : tableRow.video_content_id
                : cueCopywriting && tableRow.original_task_id 
                    ? tableRow.original_task_id 
                    : tableRow.task_id === tableRow.task_name
                        ? null
                        : tableRow.task_id,
              },
              assignmentDetails: {
                assignmentDetail: shakespeareLoc
                  ? tableRow.true_task_name
                  : tableRow.task_name,
                assignmentId: shakespeare
                  ? tableRow.task_id
                  : cueCopywriting
                    ? null
                    : staffAssignments[tableRow.task_name][geo]
                        .assignmentId ?? null,
                assignmentStatus: cueCopywriting
                  ? tableRow.status
                  : "Assigned",
                assignmentTitle: cueLoc
                  ? staffAssignments[tableRow.task_name].assignmentTitle
                  : formValues.assignmentTitle,
                contentId:
                  shakespeare || shakespeareLoc
                    ? tableRow.content_id ?? tableRow.video_content_id
                    : null,
                emailSubjectLine: shakespeare
                  ? tableRow.task_name
                  : tableRow.subject_line ?? null,
                fileLocation: formValues.fileLocation,
                genre: null,
                geo,
                keyword: tableRow.keyword ?? null,
                link: formValues.filmLink
                  ? formValues.filmLink
                  : shakespeare 
                  ? null 
                  : tableRow.shakespeare_link?.length
                  ? tableRow.shakespeare_link.find((el) => el.geo === geo).shakespeare_link
                  : tableRow.link ?? null,
                mediaType: shakespeare
                  ? formValues.mediaType
                  : tableRow.media_type,
                note: formValues.note,
                requestType: shakespeare || backstageCopywriting || tableRow.request_type === "Localization"
                  ? getFCRequestType({
                      locale: geo,
                      allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                      sourceGeo: tableRow.source_geo,
                      requestType: tableRow.request_type,
                      tableRows: tableRows,
                      shakespeare: shakespeare,
                    })
                  : tableRow.request_type,
                sourceGeo:
                  tableRow.request_type === "Adaptation" || isAdaptation({locale: geo, allLocales: tableRow.geos,sourceGeo: tableRow.source_geo, requestType: tableRow.request_type})
                  || (tableRow.source_geo === "en-GB" && tableRow.request_type === "Copywriting")
                  ? tableRow.source_geo
                  : shakespeare || cueCopywriting || isAdaptation({locale: geo, allLocales: tableRow.geos,sourceGeo: tableRow.source_geo, requestType: tableRow.request_type})
                    ? null
                    : (backstageCopywriting && !isAdaptation({locale: geo, allLocales: tableRow.geos,sourceGeo: tableRow.source_geo})) 
                      || (tableRow.request_type === "Localization" && isAdaptation({locale: geo, allLocales: tableRow.geos, sourceGeo: tableRow.source_geo, requestType: tableRow.request_type}))
                    ? isAdaptation({
                        locale: geo,
                        allLocales: tableRow.geos,
                      })
                      ? isAdaptation({
                          locale: geo,
                          getSourceLocale: true,
                        })
                      : null
                    : tableRow.source_geo,
                taskId: shakespeare
                ? tableRow.content_id
                  ? tableRow.content_id
                  : tableRow.video_content_id
                : cueCopywriting && tableRow.original_task_id 
                    ? tableRow.original_task_id 
                    : tableRow.task_id === tableRow.task_name
                        ? null
                        : tableRow.task_id,
                urgent: formValues.urgent,
                characterCount: (baseLocalization && applyLocMinRate !== 'Default' && !isAdaptation({
                    requestType: tableRow.request_type,
                    locale: geo,
                    allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                    sourceGeo: tableRow.source_geo
                  }))
                  ? staffAssignments[tableRow.task_name][geo][
                      "characterCount"
                  ]
                  : null,
                wordCount: (baseLocalization && applyLocMinRate === 'Per Unit' && !isAdaptation({
                  requestType: tableRow.request_type,
                  locale: geo,
                  allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                  sourceGeo: tableRow.source_geo
                }))
                  ? null
                  : (baseLocalization && applyLocMinRate !== 'Default' && !isAdaptation({
                    requestType: tableRow.request_type,
                    locale: geo,
                    allLocales: shakespeare ? tableRows.map((el) => el.geo) : tableRow.geos,
                    sourceGeo: tableRow.source_geo
                  }))
                  ? staffAssignments[tableRow.task_name][geo][
                      "workType1Quantity"
                  ]
                  : shakespeare ||
                    cueCopywriting ||
                    backstageCopywriting ||
                    (tableRow.request_type === "Adaptation" && !cueLoc && !amountOfEngilshAdaptations(tableRow.geos))
                      ? null
                      : tableRow.word_count,
              },
              clientId: formValues.relatedProject.client?.id ?? null,
              clientRequesterId:
                formValues.relatedProject.clientRequesters?.find((req) =>
                  shakespeare || shakespeareLoc
                    ? req.user.firstName === formValues.clientRequester ||
                      req.user.lastName === formValues.clientRequester
                    : tableRow.client_requester.includes(
                        req.user.firstName
                      ) &&
                      tableRow.client_requester.includes(req.user.lastName)
                )?.user?.id ?? null,
              projectManagerId:
                formValues.relatedProject.projectManager?.id ?? null,
              primaryId: staffDatas.find(
                (primary) =>
                  primary.name ===
                  staffAssignments[
                    tableRow[
                      `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                    ]
                  ][geo].writer
              ).user.id,
              secondaryId: staffAssignments[
                tableRow[
                  `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                ]
              ][geo].editor
                ? staffDatas.find(
                    (secondary) =>
                      secondary.name ===
                      staffAssignments[
                        tableRow[
                          `task_${
                            shakespeare || cueCopywriting ? "id" : "name"
                          }`
                        ]
                      ][geo].editor
                  ).user.id
                : null,
              qcId: staffAssignments[
                tableRow[
                  `task_${shakespeare || cueCopywriting ? "id" : "name"}`
                ]
              ][geo].reviewer
                ? staffDatas.find(
                    (qc) =>
                      qc.name ===
                      staffAssignments[
                        tableRow[
                          `task_${
                            shakespeare || cueCopywriting ? "id" : "name"
                          }`
                        ]
                      ][geo].reviewer
                  ).user.id
                : null,
              pricing: {
                purchaseOrderId: formValues.po?.id ?? null,
                workTypes,
              },
            },
          );

          if (!cueCopywriting) {
            // process the GEO-related data for Slack notification
            const getPersonTag = (role) => {
                const person = staffDatas.find(
                    (p) =>
                      staffAssignments[
                        tableRow[`task_${shakespeare ? "id" : "name"}`]
                      ][geo][role]?.includes(p.user.firstName) &&
                      staffAssignments[
                        tableRow[`task_${shakespeare ? "id" : "name"}`]
                      ][geo][role]?.includes(p.user.lastName)
                  ),
                  slackTag = person?.[cueLoc ? "cueSlackID" : "slackID"];
                return slackTag
                  ? `<@${slackTag}>`
                  : person?.user?.firstName
                  ? `${person.user.firstName} ${person.user.lastName}`
                  : null;
              },
              [writer, editor, reviewer] = [
                getPersonTag("writer"),
                getPersonTag("editor"),
                getPersonTag("reviewer"),
              ],
              geos = notification.geos.map((el) => Object.keys(el)[0]);

            if (geos.includes(geo)) {
              const geoStaffChecker = (staffGroup, staffMember) => {
                if (
                  notification.geos[getGeoIndex(geo, notification.geos)][
                    geo
                  ].hasOwnProperty(staffGroup)
                ) {
                  if (
                    !notification.geos[getGeoIndex(geo, notification.geos)][
                      geo
                    ][staffGroup].includes(staffMember)
                  )
                    notification.geos[getGeoIndex(geo, notification.geos)][geo][
                      staffGroup
                    ].push(staffMember);
                } else {
                  Object.assign(
                    notification.geos[getGeoIndex(geo, notification.geos)][geo],
                    {
                      [staffGroup]: [staffMember],
                    }
                  );
                }
              };
              if (writer) geoStaffChecker("writers", writer);
              if (editor) geoStaffChecker("editors", editor);
              if (reviewer) geoStaffChecker("reviewers", reviewer);
            } else {
              let geoObj = { [geo]: {} };
              if (writer) Object.assign(geoObj[geo], { writers: [writer] });
              if (editor) Object.assign(geoObj[geo], { editors: [editor] });
              if (reviewer)
                Object.assign(geoObj[geo], { reviewers: [reviewer] });
              if (Object.keys(geoObj[geo]).length)
                notification.geos.push(geoObj);
            }
          }
          if (cueLoc) applyIndividualGeoDeadlines(); // add individual deadlines (Non-FC notification)
        }
      );
      if (cueLoc) {
        nonFcNotifications.push({
          ...notification,
          assignment: tableRow.task_name,
          assignmentDetails: {
            ...notification.assignmentDetails,
            requestType: tableRow.request_type,
            assignmentTitle:
              staffAssignments[tableRow.task_name].assignmentTitle,
            wordCount: tableRow.word_count,
            taskId: tableRow.task_id,
          },
        });
        notification.geos = [];
      }
    });

    if (!cueLoc && !cueCopywriting) {
      // add assignments details
      Object.entries(staffAssignments).forEach((el) => {
        const assgnDetail = shakespeare
            ? staffAssignments[el[0]].task_name
            : el[0],
          geos = shakespeare
            ? [Object.keys(el[1])[0]]
            : Object.keys(el[1]);
        geos.forEach((geo) => {
          if (geo !== "wordCount") {
            notification.geos[getGeoIndex(geo, notification.geos)][
              geo
            ].hasOwnProperty("assignments")
              ? notification.geos[getGeoIndex(geo, notification.geos)][
                  geo
                ].assignments.push(assgnDetail)
              : Object.assign(
                  notification.geos[getGeoIndex(geo, notification.geos)][geo],
                  {
                    assignments: [assgnDetail],
                  }
                );
          }
        });
      });
      applyIndividualGeoDeadlines(); // add individual deadlines (FC notification)
    }

    const enAdaptations = ["en-AU", "en-CA", "en-GB"];
    const adaptationNotifications = _.cloneDeep(notification);
    adaptationNotifications.geos = [];
    promiseList.forEach((el) => {
      if (el.assignmentDetails?.requestType === "Adaptation" && enAdaptations.includes(el.assignmentDetails?.geo)) {
        const index = notification.geos.findIndex(obj => el.assignmentDetails?.geo in obj);
        if (index !== -1) {
          const removedObject = notification.geos.splice(index, 1)[0];
          adaptationNotifications.geos.push(removedObject);
        }
      }
      if (el.assignmentDetails && el.assignmentDetails?.requestType === "Adaptation" && el.assignmentDetails?.geo === "de-CH") {
        el.assignmentDetails.sourceGeo = "de-DE";
      }
    });

    const enCopywriting = ["en-AU", "en-GB"];
    const copywritingNotifications = _.cloneDeep(notification);
    copywritingNotifications.geos = [];
    promiseList.forEach((el) => {
      if (el.assignmentDetails?.requestType === "Copywriting" && enCopywriting.includes(el.assignmentDetails?.geo)) {
        const index = notification.geos.findIndex(obj => el.assignmentDetails?.geo in obj);
        if (index !== -1) {
          const removedObject = notification.geos.splice(index, 1)[0];
          copywritingNotifications.geos.push(removedObject);
        }
      }
    });

    await processAssignmentsList(promiseList)
      .then((res) => {
        const failedRecords = [];
        if (res.status > 204) {
          failedRecords.push(res);
        }
        if (failedRecords.length) {
          resolve(
            failedRecords[0].data && failedRecords[0].data.statusCode ?
            `${failedRecords[0].data.statusCode}: ${failedRecords[0].data.message}` :
            `Something went wrong!`
          );
        } else {
          if (!cueCopywriting) {
            if (cueLoc) {
              const slackPromises = nonFcNotifications.map((post) => {
                const assignment = res.data.find(el => 
                  el.assignmentDetails?.assignmentDetail === post.assignment && 
                  el.assignmentDetails?.wordCount === post.assignmentDetails?.wordCount);
                const assignmentId = assignment.id;
                publishMessage({
                  mode: "nonFcNotification",
                  item: { ...post, id: assignmentId }
                })
              });
              Promise.all(slackPromises).catch((err) => reject(err));
            } else {
              if (notification.geos.length) {
                const geoKeys = notification.geos.map(el => Object.keys(el)[0]);
                if (geoKeys 
                    && geoKeys.length 
                    && geoKeys.includes("de-CH") 
                    && geoKeys.includes("de-DE")
                    && (notification.assignmentDetails.requestType === "Copywriting" || notification.assignmentDetails.requestType === "Adaptation")
                    && notification.assignmentDetails.fileLocation === "https://wrkflow.app/") {
                  const deCHContentId = promiseList.find(el => el.assignmentDetails.geo === "de-CH")?.assignmentDetails?.contentId;
                  const deDEContentId = promiseList.find(el => el.assignmentDetails.geo === "de-DE")?.assignmentDetails?.contentId;
                  if ((deCHContentId && deDEContentId && deCHContentId === deDEContentId) || backstageCopywriting) {
                    const notificationClone = _.cloneDeep(notification);
                    const notIndex = notification.geos.findIndex(obj => obj["de-CH"]);
                    const removedObject = notification.geos.splice(notIndex, 1)[0];
                    notificationClone.geos = [removedObject];
                    publishMessage({
                      mode: "fcNotification",
                      item: notificationClone,
                      modifier: "de-CH-adaptation",
                    })
                  }
                }
                publishMessage({
                  mode: "fcNotification",
                  item: notification,
                })
              }
              if (adaptationNotifications.geos.length) {
                publishMessage({
                  mode: "fcNotification",
                  item: adaptationNotifications,
                  modifier: "adaptation",
                })
              }
              if (copywritingNotifications.geos.length) {
                publishMessage({
                  mode: "fcNotification",
                  item: copywritingNotifications,
                  modifier: "copywriting",
                })
              }
            }
          } else if (cueCopywriting && promiseList[0].projectId === 91) {
            const data = [];
            promiseList.forEach((el) => {
              const existedPrimary = data.find(item => item.primary.user.id === el.primaryId);
              if (!existedPrimary) {
                const item = {
                  primary: staffDatas.find(user => user.user.id === el.primaryId),
                  secondary: el.secondaryId ? staffDatas.find(user => user.user.id === el.secondaryId) : null,
                  qc: el.qcId ? staffDatas.find(user => user.user.id === el.qcId) : null,
                  geo: [el.assignmentDetails?.geo],
                  link: el.assignmentDetails?.link,
                  files: [{
                    payPeriod: el.schedule?.payPeriod,
                    project: res.data[0].project,
                    assignmentDetails: el.assignmentDetails?.assignmentDetail
                  }],
                  link: res.data[0].assignmentDetails?.link
                }
                data.push(item);
              } else {
                if (!existedPrimary.geo.includes(el.assignmentDetails?.geo)) {
                  existedPrimary.geo.push(el.assignmentDetails?.geo);
                }
                existedPrimary.files.push({
                  payPeriod: el.schedule?.payPeriod,
                  project: res.data[0].project,
                  assignmentDetails: el.assignmentDetails?.assignmentDetail
                })
              }
            })
            publishMessage({
              mode: "filmTvMessages",
              item: notification,
              data: data
            }) 
          } else if (isDubEditor) {
            const resData = res.data;
            resData.forEach((item) => {
              ["primary", "secondary", "qc"].forEach((el) => {
                if (item[el]) {
                  item[el] = staffDatas.find(user => user.user.id === item[el].id);
                }
              })
            })
            const dubEditorItem = {
              titlesList: [...new Set(res.data.map((item) => item.assignmentDetails.assignmentTitle))],
              primaryDue: `${res.data[0].schedule.primaryDueDate.split('-').reverse().slice(0, 2).reverse().join('/')}/${res.data[0].schedule.primaryDueDate.split('-')[0]} ${res.data[0].schedule.primaryDueTime}`,
              secondaryDue: res.data[0].schedule.secondaryDueDate ? `${res.data[0].schedule.secondaryDueDate.split('-').reverse().slice(0, 2).reverse().join('/')}/${res.data[0].schedule.secondaryDueDate.split('-')[0]} ${res.data[0].schedule.secondaryDueTime}` : null,
              qcDue: res.data[0].schedule.qcDueDate ? `${res.data[0].schedule.qcDueDate.split('-').reverse().slice(0, 2).reverse().join('/')}/${res.data[0].schedule.qcDueDate.split('-')[0]} ${res.data[0].schedule.qcDueTime}` : null,
            };
            publishMessage({
              mode: "dubEditorMessages",
              item: dubEditorItem,
              data: res.data
            }) 
          }
          resolve("Import Successful");
        }
      })
      .catch((err) => reject(err));
  });
};

export function parseAssignmentResponse(data) {
  const pm = data.projectManager
    ? {
        ...data.projectManager,
        name: `${data.projectManager.firstName || ""} ${data.projectManager.lastName || ""}`.trim(),
        initials: `${(data.projectManager.firstName || "").charAt(0)}${(data.projectManager.lastName || "").charAt(0)}`.toUpperCase(),
      }
    : null;
  return {
    id: data.id || null,

    taskId: data.task?.taskId || null,
    // Record id of the parent task — used by the hierarchy panel to load the
    // task and its sibling assignments. Falls back to null when the payload
    // only carries the readable Cue Task ID (the panel then resolves it).
    taskDbId: data.task?.id ?? null,
    cueTaskId: data.cueTaskId ?? null,

    assignmentTitle: data.assignmentDetails?.assignmentTitle || null,
    assignmentDetail: data.assignmentDetails?.assignmentDetail || null,
    externalAssignmentId: data.assignmentDetails?.assignmentId || null,
    contentId: data.assignmentDetails?.contentId || null,
    requestType: data.assignmentDetails?.requestType || null,
    assignmentType: null,

    sourceGeo: data.assignmentDetails?.sourceGeo || null,
    geo: data.assignmentDetails?.geo || null,

    taskName: "",
    department: data.department ?? null,
    note: data.assignmentDetails?.note || null,
    urgent: data.assignmentDetails?.urgent ?? null,

    // Read back so a full update re-sends them instead of blanking them.
    clientFeedback: data.assignmentDetails?.clientFeedback ?? null,
    clientReviewed: data.assignmentDetails?.clientReviewed ?? null,
    slackTs: data.assignmentDetails?.slackTs ?? null,
    primaryAccepted: data.assignmentDetails?.primaryAccepted ?? null,
    secondaryAccepted: data.assignmentDetails?.secondaryAccepted ?? null,

    payPeriod: data.schedule?.payPeriod || "",

    status: data.assignmentDetails?.assignmentStatus || "Requested",

    isBilled: data.assignmentDetails?.billed || false,

    priority: data.project?.priority || null,

    fileLocation: data.assignmentDetails?.fileLocation || null,
    emailSubjectLine: data.assignmentDetails?.emailSubjectLine || "",

    mediaType: data.assignmentDetails?.mediaType || null,
    keyword: data.assignmentDetails?.keyword || null,
    genre: data.assignmentDetails?.genre || "",

    wordCount: data.assignmentDetails?.wordCount || "",
    characterCount: data.assignmentDetails?.characterCount || "",

    relatedProject: data.project || { name: "Empty" },

    clientRequester: {
      user: data.clientRequester || {
        firstName: "Empty",
        lastName: ""
      }
    },

    purchaseOrder: data.pricing?.purchaseOrder || null,
    projectManager: pm,

    overrideStaffAvailability: false,
    overrideDueDates: false,

    deliveryDate: data.schedule?.deliveryDate || null,
    deliveryTime: data.schedule?.deliveryTime || null,

    requestDate: data.schedule?.requestDate || null,
    requestTime: data.schedule?.requestTime || null,

    primary: data.primary || null,
    secondary: data.secondary || null,
    qc: data.qc || null,

    schedule: {
      primaryDueDate: data.schedule?.primaryDueDate || null,
      primaryDueTime: data.schedule?.primaryDueTime || null,
      secondaryDueDate: data.schedule?.secondaryDueDate || null,
      secondaryDueTime: data.schedule?.secondaryDueTime || null,
      qcDueDate: data.schedule?.qcDueDate || null,
      qcDueTime: data.schedule?.qcDueTime || null
    },

    pricingModel: null,

    pricing: {
      purchaseOrder: data.pricing?.purchaseOrder || null,
      workTypes: data.pricing?.workTypes || [],
      primaryTotal: data.pricing?.primaryTotal ?? null,
      secondaryTotal: data.pricing?.secondaryTotal ?? null,
      clientTotal: data.pricing?.clientTotal ?? null
    },

    // Notes & Files tab — the files already attached to the assignment plus the
    // assignment link. All three keys must always be present so the tab can add
    // to them reactively. A freshly read record has nothing pending removal.
    documents: mapAttachedDocuments(data),
    removedDocuments: [],
    links: data.assignmentDetails?.link ? [data.assignmentDetails.link] : []
  };
};
